import { useLingui } from '@lingui/react/macro';
import { Pencil, Trophy } from 'lucide-react';
import { getGameEventTypeLabel } from 'modules/games/domain/constants/game-event-type-labels';
import { getPlayerPositionLabel } from 'modules/teams/domain/constants/player-position-labels';
import { useCallback } from 'react';
import { theme } from 'styles/theme/theme';

import EmptyButton from 'ud-ui/components/empty-button';
import FootballCardIcon from 'ud-ui/components/icon/football-card-icon';
import FootballCornerIcon from 'ud-ui/components/icon/football-corner-icon';
import FootballIcon from 'ud-ui/components/icon/football-icon';
import FootballPassAndGoalIcon from 'ud-ui/components/icon/football-pass-and-goal-icon';
import FootballPenaltyIcon from 'ud-ui/components/icon/football-penalty-icon';
import FreeKickIcon from 'ud-ui/components/icon/free-kick-icon';
import MossedPenaltyIcon from 'ud-ui/components/icon/missed-penalty-icon';
import OwnGoalIcon from 'ud-ui/components/icon/own-goal-icon';
import ReplacementIcon from 'ud-ui/components/icon/replacement-icon';
import TimeoutIcon from 'ud-ui/components/icon/timeout-icon';
import UDTippy from 'ud-ui/components/tippy';
import { GameDetailsTeamMember, GameEvent, GameEventType } from 'modules/games/domain/interfaces/game-details';
import * as styles from './game-result-events.list.styles';

interface GameResultEventIconProps {
  type: GameEventType;
}

export const GameResultEventIcon = (props: GameResultEventIconProps) => {
  const width = 20;
  const height = 20;

  switch (props.type.toLowerCase()) {
    case GameEventType.GOAL.toLowerCase():
    case GameEventType.AFTER_MATCH_PENALTY_GOAL.toLowerCase():
      return <FootballIcon width={width} height={height} />;
    case GameEventType.PASS_AND_GOAL.toLowerCase():
      return <FootballPassAndGoalIcon width={30} height={28} />;
    case GameEventType.CORNER.toLowerCase():
      return <FootballCornerIcon width={24} height={28} />;
    case GameEventType.FOUL.toLowerCase():
      return <FreeKickIcon width={20} height={26} />;
    case GameEventType.PENALTY.toLowerCase():
      return <FootballPenaltyIcon width={31} height={24} />;
    case GameEventType.RED_CARD.toLowerCase():
      return <FootballCardIcon width={width} height={height} type="red" />;
    case GameEventType.YELLOW_CARD.toLowerCase():
      return <FootballCardIcon width={width} height={height} type="yellow" />;
    case GameEventType.REPLACEMENT.toLowerCase():
      return <ReplacementIcon width={width} height={height} />;
    case GameEventType.TIMEOUT.toLowerCase():
      return <TimeoutIcon width={20} height={25} />;
    case GameEventType.OWN_GOAL.toLowerCase():
      return <OwnGoalIcon width={40} height={14} />;
    case GameEventType.AFTER_MATCH_PENALTY_MISSED.toLowerCase():
      return <MossedPenaltyIcon width={16} height={16} />;
    default:
      return <Trophy width={width} height={height} color={theme.colors.AccentNaplesYellow} />;
  }
};

interface GameResultEventProps {
  event: GameEvent;
  members: Record<number, GameDetailsTeamMember>;
  isLeft: boolean;
  onEditClick?: (eventId: number) => void;
}

export const GameResultEvent = (props: GameResultEventProps) => {
  const { t } = useLingui();

  const { event, members, isLeft, onEditClick } = props;
  const eventTypeLabel = getGameEventTypeLabel(event.type);
  const positionName = (position?: string) => {
    const label = getPlayerPositionLabel(position);
    return label ? t(label) : position;
  };
  const className = `game-event ${isLeft ? 'left' : 'right'}`;

  const handleEditClick = useCallback(() => {
    if (onEditClick) {
      onEditClick(event.id);
    }
  }, [event, onEditClick]);

  return (
    <div className={className}>
      <div></div>
      <styles.GameResultEventMinute minutes={event.minutes} extraMinutes={event.extraMinutes} isLeft={isLeft}>
        <div className="game-event-time">
          <div className="minutes">{event.minutes}’</div>
          {event.extraMinutes && <div className="extra-minutes">+{event.extraMinutes}</div>}
        </div>

        <div className="game-event-card">
          <div className="game-event-icon">
            <GameResultEventIcon type={event.type} />
          </div>
          <div className="game-event-body">
            <div className="game-event-type flex flex-row">
              {eventTypeLabel ? t(eventTypeLabel) : event.type}
              <UDTippy content={t`Редактировать`} placement={'top'}>
                <EmptyButton style={{ marginLeft: 8 }} onClick={handleEditClick}>
                  <Pencil size={14} />
                </EmptyButton>
              </UDTippy>
            </div>
            {event.playerIds.map((playerId) => (
              <div key={playerId} className="game-event-player">
                <div className="game-event-player-info">
                  {members[playerId]?.lastName ?? t`Неизвестный`} {members[playerId]?.firstName ?? t`Неизвестный`}
                </div>
                <div className="game-event-player-position">
                  #{members[playerId]?.number ?? '?'}, {members[playerId] ? positionName(members[playerId]?.position) : t`Неизвестно`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </styles.GameResultEventMinute>
      <div></div>
    </div>
  );
};
